import { Job, JobStatus, statusOrder } from "./JobBoard";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Trash2, ExternalLink, Edit, MoreVertical, Calendar } from "lucide-react";
import { cn } from "@/lib/utils";
import { colors, commonStyles } from "@/lib/theme";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/components/ui/use-toast";
import { useState } from "react";
import { format } from "date-fns";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";

interface JobCardProps {
  job: Job;
  onDelete: (id: string) => void;
  onEdit: (job: Job) => void;
  onStatusChange: () => void;
  categories: { id: string; name: string; user_id: string }[];
  jobCategories: string[];
  onCategoryChange: (jobId: string, categoryId: string) => void;
  isDarkMode?: boolean;
}

const statusStyles: Record<JobStatus, string> = {
  Interested: `bg-[${colors.status.interested.bg}] text-[${colors.status.interested.text}]`,
  Applied: `bg-[${colors.status.applied.bg}] text-[${colors.status.applied.text}]`,
  Interview: `bg-[${colors.status.interview.bg}] text-[${colors.status.interview.text}]`,
  Offer: `bg-[${colors.status.offer.bg}] text-[${colors.status.offer.text}]`,
  Rejected: `bg-[${colors.status.rejected.bg}] text-[${colors.status.rejected.text}]`,
  Archived: `bg-[${colors.status.archived.bg}] text-[${colors.status.archived.text}]`,
};

export function JobCard({ 
  job, 
  onDelete, 
  onEdit, 
  onStatusChange, 
  categories, 
  jobCategories, 
  onCategoryChange,
  isDarkMode = true
}: JobCardProps) {
  const { toast } = useToast();
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);

  const handleStatusChange = async (newStatus: JobStatus) => {
    const { error } = await supabase
      .from('jobs')
      .update({ status: newStatus })
      .eq('id', job.id);

    if (error) {
      toast({
        title: "Error updating status",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    onStatusChange();
  };

  const handleDelete = () => {
    setShowDeleteAlert(true);
  };

  const confirmDelete = () => {
    onDelete(job.id);
    setShowDeleteAlert(false);
  };

  // Get available status options based on current status
  const availableStatuses = job.status === "Archived" 
    ? statusOrder.filter(status => status !== "Archived") // Show all non-archive options when archived
    : statusOrder; // Show all options including archive when not archived

  return (
    <>
      <Card className={cn(
        commonStyles.card,
        "bg-[#2C2E2D] border-[#818881]",
        job.status === "Archived" && "opacity-75"
      )}>
        <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
          <div 
            className="flex items-center space-x-4 cursor-pointer"
            onClick={() => window.open(job.url, "_blank")}
          >
            {job.logoUrl && (
              <img
                src={job.logoUrl}
                alt={`${job.company_name} logo`}
                className="w-10 h-10 rounded-full object-contain bg-[#2C2E2D]"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            )}
            <div>
              <h3 className="font-serif font-semibold leading-none text-[#D9D9D9]">{job.job_title}</h3>
              <p className="text-sm text-[#818881]">{job.company_name}</p>
              {job.deadline && (
                <div className={cn(
                  "flex items-center mt-1 text-xs",
                  new Date(job.deadline) < new Date() ? "text-rose-400" : "text-[#818881]"
                )}>
                  <Calendar className="h-3 w-3 mr-1" />
                  <span>
                    {new Date(job.deadline) < new Date() 
                      ? `Deadline passed: ${format(new Date(job.deadline), 'MMM d, yyyy')}`
                      : `Deadline: ${format(new Date(job.deadline), 'MMM d, yyyy')}`
                    }
                  </span>
                </div>
              )}
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className={cn(commonStyles.button.ghost, "text-[#D9D9D9] hover:text-[#FFFFFF]")}>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className={cn(commonStyles.dropdown, "bg-[#2C2E2D] border-[#818881]")}>
              <DropdownMenuItem onClick={() => onEdit(job)} className={cn(commonStyles.dropdownItem, "text-[#D9D9D9] hover:bg-[#818881]/20")}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => window.open(job.url, "_blank")} className={cn(commonStyles.dropdownItem, "text-[#D9D9D9] hover:bg-[#818881]/20")}>
                <ExternalLink className="mr-2 h-4 w-4" />
                View Job
              </DropdownMenuItem>
              <DropdownMenuSeparator className={cn(commonStyles.dropdownSeparator, "bg-[#818881]")} />
              {availableStatuses.map((status) => (
                <DropdownMenuItem
                  key={status}
                  onClick={() => handleStatusChange(status)}
                  disabled={status === job.status}
                  className={cn(commonStyles.dropdownItem, "text-[#D9D9D9] hover:bg-[#818881]/20")}
                >
                  <span className={cn(
                    "w-2 h-2 rounded-full mr-2",
                    status === "Interested" && `bg-[${colors.status.interested.bg}]`,
                    status === "Applied" && `bg-[${colors.status.applied.bg}]`,
                    status === "Interview" && `bg-[${colors.status.interview.bg}]`,
                    status === "Offer" && `bg-[${colors.status.offer.bg}]`,
                    status === "Rejected" && `bg-[${colors.status.rejected.bg}]`,
                    status === "Archived" && `bg-[${colors.status.archived.bg}]`
                  )} />
                  {status === "Archived" ? "Archive" : status}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator className={cn(commonStyles.dropdownSeparator, "bg-[#818881]")} />
              <DropdownMenuItem
                onClick={handleDelete}
                className={cn(commonStyles.dropdownItem, "text-[#D9D9D9] hover:bg-[#818881]/20")}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </CardHeader>
        <CardContent>
          <div 
            className="cursor-pointer"
            onClick={() => window.open(job.url, "_blank")}
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <p className="text-sm text-[#818881] mb-4 line-clamp-2 hover:cursor-help">
                  {job.description}
                </p>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs bg-[#2C2E2D] text-[#D9D9D9] border-[#818881]">
                {job.description}
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="flex items-center justify-between">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <span 
                  className={cn(
                    "px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer hover:ring-2 hover:ring-offset-2 hover:ring-gray-800 transition-all", 
                    statusStyles[job.status]
                  )}
                >
                  {job.status}
                </span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className={commonStyles.dropdown}>
                {availableStatuses.map((status) => (
                  <DropdownMenuItem
                    key={status}
                    onClick={() => handleStatusChange(status)}
                    disabled={status === job.status}
                    className={commonStyles.dropdownItem}
                  >
                    <span className={cn(
                      "w-2 h-2 rounded-full mr-2",
                      status === "Interested" && `bg-[${colors.status.interested.bg}]`,
                      status === "Applied" && `bg-[${colors.status.applied.bg}]`,
                      status === "Interview" && `bg-[${colors.status.interview.bg}]`,
                      status === "Offer" && `bg-[${colors.status.offer.bg}]`,
                      status === "Rejected" && `bg-[${colors.status.rejected.bg}]`,
                      status === "Archived" && `bg-[${colors.status.archived.bg}]`
                    )} />
                    {status === "Archived" ? "Archive" : status}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                className={commonStyles.button.ghostPrimary}
                onClick={() => onEdit(job)}
              >
                <Edit className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={commonStyles.button.ghostDestructive}
                onClick={handleDelete}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="flex items-center mt-2">
            <Label htmlFor={`category-${job.id}`} className="text-sm mr-2">Category:</Label>
            <Select
              value={jobCategories.find(categoryId => 
                categories.some(cat => cat.id === categoryId)
              ) || "none"}
              onValueChange={(value) => onCategoryChange(job.id, value)}
            >
              <SelectTrigger 
                id={`category-${job.id}`}
                className={cn(
                  "h-8 text-xs",
                  isDarkMode 
                    ? "bg-[#818881]/20 border-[#818881] text-[#D9D9D9]" 
                    : "bg-white"
                )}
              >
                <SelectValue placeholder="Assign category" />
              </SelectTrigger>
              <SelectContent className={cn(
                isDarkMode ? "bg-[#2C2E2D] border-[#818881]" : "bg-white"
              )}>
                <SelectItem value="none" className={isDarkMode ? "text-[#D9D9D9] focus:bg-[#818881]/20 focus:text-[#FFFFFF]" : ""}>None</SelectItem>
                {categories.map(category => (
                  <SelectItem 
                    key={category.id} 
                    value={category.id}
                    className={isDarkMode ? "text-[#D9D9D9] focus:bg-[#818881]/20 focus:text-[#FFFFFF]" : ""}
                  >
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={showDeleteAlert} onOpenChange={setShowDeleteAlert}>
        <AlertDialogContent className={commonStyles.dialog}>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#D9D9D9]">Are you sure?</AlertDialogTitle>
            <AlertDialogDescription className={commonStyles.dialogDescription}>
              This will permanently delete the job "{job.job_title}" at {job.company_name}.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className={commonStyles.dialogCancel}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className={commonStyles.dialogAction}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}